import { styled } from "@mui/material/styles";
import { SearchMatchScope } from "_shared/_views/tables/search-match";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import { useLoadPastFilteredRows } from "_shared/_hooks/infinite-scroll/use-load-past-filtered-rows";
import { MRTNoMemo } from "_shared/_views/tables/table";
import {
  ROW_CHOICE_CELL_PROPS,
  ROW_CHOICE_TABLE_OPTIONS,
  RowChoiceButton,
  RowChoicePerson,
  rowChoiceRowProps,
  type RowChoiceUnavailableReason,
} from "_shared/_views/tables/row-choice";
import {
  isInExcludedLegalEntity,
  useRowChoiceExcludedLegalEntity,
} from "_shared/_views/tables/row-choice-excluded-legal-entity";
import { useRowChoiceRequiresCredentials } from "_shared/_views/tables/row-choice-requires-credentials";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import DebouncedTextField from "_shared/_views/text-input/debounced-text-field";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import type { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import {
  credentialsMissingReason,
  isKnownToLackCredentials,
} from "marketplace/individuals/profiles/professional-credentials";
import { createMRTColumnHelper, type MRT_TableOptions } from "material-react-table";
import { useId, useMemo, useRef, useState } from "react";
import UserInformation from "users/information/user-information";
import NetworkConnectionQuery, {
  NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH,
} from "users/networking/connections/_api/queries/network-connection-query";
import useInfiniteNetworkConnections from "users/networking/connections/_hooks/use-infinite-network-connections";

const NetworkTableRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  height: "100%",
  "& .NetworkedIndividuals-tableContainer": {
    flex: 1,
  },
  "& .NetworkedIndividuals-tablePaper": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
}));
const ErrorContent = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
}));

type NetworkedIndividualsProps = {
  selectedMemberUserIds?: Guid[];
  onSelected: (rowData: UserInformation) => void;
};

const columnHelper = createMRTColumnHelper<UserInformation>();

/** "Partner · Northwind LLP", or whichever of the two a connection has. */
function describeRole(connection: UserInformation): string | undefined {
  // `valueOf`, not `toString`, which is the title's debugging form.
  const parts = [connection.title?.valueOf(), connection.companyName].filter((part): part is string =>
    Boolean(part?.trim()),
  );
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/**
 * The connections' profiles, by user id. One that will not parse is left out rather than allowed to
 * take the picker down with it: all it costs is the picker not knowing that person's credentials.
 */
function byUserId(profiles: readonly IndividualJSON[] | undefined): Record<string, Individual> {
  const individuals: Record<string, Individual> = {};
  for (const json of profiles ?? []) {
    try {
      const individual = Individual.fromJSON(json);
      if (individual) individuals[individual.userId.value] = individual;
    } catch (error) {
      console.warn("A network connection's profile could not be read", error);
    }
  }
  return individuals;
}

export default function NetworkedIndividuals(props: Readonly<NetworkedIndividualsProps>) {
  const {
    selectedMemberUserIds,
    onSelected
  } = props;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  // A stability guarantee, not defensive memoization: the hook starts again from the first page
  // whenever the query changes, so a new query on every render would never get past it.
  const query = useMemo(() => new NetworkConnectionQuery({ query: search }), [search]);
  const { connections, isLoading, isInitialLoading, error, loadMore, retry } = useInfiniteNetworkConnections(query);
  useInfiniteScroll(loadMore, connections.hasNextPage, isLoading, tableContainerRef);
  const requiresCredentials = useRowChoiceRequiresCredentials();
  const excludedLegalEntityId = useRowChoiceExcludedLegalEntity();
  const reasonIdPrefix = useId();

  // A team will not take a member whose profile lists no professional credentials (#5800). A
  // connection is a user, not a profile, so where the choice seats them the picker asks for the
  // profiles of the connections on the loaded pages — and only there, because nothing else on this
  // tab needs them. A profile that does not arrive decides nothing: the API still refuses the save.
  const connectionUserIds = useMemo(
    () => new Set(connections.data.map((connection) => connection.id?.value).filter((id): id is string => !!id)),
    [connections.data],
  );
  const connectionProfilesQuery = useGetIndividualsByUserIdsQuery(
    { userIds: [...connectionUserIds] },
    { skip: !requiresCredentials || connectionUserIds.size === 0 },
  );
  const individualsByUserId = useMemo(() => byUserId(connectionProfilesQuery.data), [connectionProfilesQuery.data]);

  // The connections this picker offers: everyone loaded, less the firm it leaves out (#6091).
  const offeredConnections = useMemo(
    () => connections.data.filter((connection) => !isInExcludedLegalEntity(excludedLegalEntityId, connection.legalEntityId)),
    [connections.data, excludedLegalEntityId],
  );
  // The table may have nothing left to scroll once those are out, so keep loading past them.
  useLoadPastFilteredRows(
    offeredConnections.length < connections.data.length,
    connections.hasNextPage,
    isLoading,
    Boolean(error),
    loadMore,
    tableContainerRef,
  );

  function isAlreadyChosen(connection: UserInformation): boolean {
    return selectedMemberUserIds?.some((id) => id.isEqualTo(connection.id)) ?? false;
  }

  function lacksCredentials(connection: UserInformation): boolean {
    return requiresCredentials && isKnownToLackCredentials(individualsByUserId[connection.id.value]);
  }

  function getUnavailableReason(connection: UserInformation | undefined): RowChoiceUnavailableReason | undefined {
    if (!connection?.id || isAlreadyChosen(connection) || !lacksCredentials(connection)) return undefined;
    return { id: `${reasonIdPrefix}-${connection.id.value}`, text: credentialsMissingReason };
  }

  // Undefined for somebody already chosen or a team would refuse, which is what makes their row and
  // its button say so. MRT asks for the props of the placeholder rows it draws while loading too,
  // whose `original` is an empty object, so a row with no record behind it is one that cannot be
  // chosen rather than a crash.
  function getChoice(connection: UserInformation | undefined): (() => void) | undefined {
    if (!connection?.id || isAlreadyChosen(connection) || lacksCredentials(connection)) return undefined;
    return () => onSelected(connection);
  }

  const columns = [
    columnHelper.accessor((connection) => connection.name?.fullName ?? "", {
      header: "Name",
      id: "name",
      Cell: ({ row }) => (
        <RowChoicePerson
          individual={row.original.toIndividual()}
          name={row.original.name?.fullName ?? "Unknown connection"}
          // The search reads name, email and company; the role line also holds the title, so only the
          // email is marked under the name.
          details={[describeRole(row.original), { text: row.original.email?.toString(), searched: true }]}
          unavailableReason={getUnavailableReason(row.original)}
        />
      ),
    }),
    columnHelper.display({
      id: "choose",
      header: "",
      muiTableBodyCellProps: ROW_CHOICE_CELL_PROPS,
      Cell: ({ row }) => (
        <RowChoiceButton
          name={row.original.name?.fullName ?? "this connection"}
          onChoose={getChoice(row.original)}
          alreadyChosen={isAlreadyChosen(row.original)}
          describedBy={getUnavailableReason(row.original)?.id}
        />
      ),
    }),
  ];

  const tableOptions: MRT_TableOptions<UserInformation> = {
    ...ROW_CHOICE_TABLE_OPTIONS,
    columns,
    data: offeredConnections,
    muiTableBodyRowProps: ({ row }) => rowChoiceRowProps(getChoice(row.original)),
    manualFiltering: true,
    enableStickyHeader: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: error
      ? {
        color: "error",
        children: (
          <ErrorContent>
            Error loading data
            <Button color="inherit" size="small" onClick={retry}>Retry</Button>
          </ErrorContent>
        ),
      }
      : undefined,
    muiTableContainerProps: { className: "NetworkedIndividuals-tableContainer", ref: tableContainerRef },
    muiTablePaperProps: { className: "NetworkedIndividuals-tablePaper" },
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: Boolean(error),
      isLoading: isInitialLoading,
      onRetry: retry,
      subject: "connections",
    }),
    state: {
      isLoading: isInitialLoading,
      showAlertBanner: Boolean(error),
      showProgressBars: isLoading && !isInitialLoading,
    },
  }

  return (
    <NetworkTableRoot>
      <DebouncedTextField
        label="Search your network"
        size="small"
        type="search"
        value={search}
        onDebouncedChange={setSearch}
        slotProps={{ htmlInput: { maxLength: NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH } }}
      />
      {/* The picker's own search box, lent to the names it marks (#6590). */}
      <SearchMatchScope term={search}>
        <MRTNoMemo<UserInformation> options={tableOptions} />
      </SearchMatchScope>
    </NetworkTableRoot>
  );
}
